import React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormControlLabel,
  Checkbox,
  Grid,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import { getTodayStartOfDay } from 'utils/dateUtils';
import ValueTypeEnums from '../ValueTypeEnums';

export interface ValueFormData {
  headline: string;
  content: string;
  value_type: string;
  applicable_for: string[];
  end_date: string;
  is_active: boolean;
  label: string;
}

export interface ValueFormErrors {
  headline?: string;
  content?: string;
  value_type?: string;
  applicable_for?: string;
  end_date?: string;
  label?: string;
}

interface ValueFormProps {
  formData: ValueFormData;
  onFormChange: (field: string, value: any) => void;
  errors?: ValueFormErrors;
  disabled?: boolean;
}

const applicableForOptions = ['CPO', 'New', 'Used', 'All'];

// Helper function to get minimum date (today)
const getMinDate = () => getTodayStartOfDay();

const ValueForm: React.FC<ValueFormProps> = ({
  formData,
  onFormChange,
  errors = {},
  disabled = false,
}) => {
  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | { target: { name: string; value: any } },
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;
    onFormChange(
      name,
      type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    );
  };

  const handleApplicableForChange = (event: any) => {
    const value = event.target.value;
    onFormChange(
      'applicable_for',
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const handleDateChange = (newValue: DateTime | null) => {
    onFormChange(
      'end_date',
      newValue ? newValue.startOf('day').toUTC().toISO() ?? '' : '',
    );
  };

  return (
    <Grid container spacing={3}>
      {/* Basic Information */}
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Basic Information
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Headline *
          </Typography>
          <TextField
            fullWidth
            name="headline"
            value={formData.headline}
            onChange={handleChange}
            variant="outlined"
            placeholder="Enter headline for the value content"
            size="small"
            error={!!errors.headline}
            helperText={errors.headline}
            disabled={disabled}
            inputProps={{
              sx: { py: 1.5 },
            }}
          />
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Message *
          </Typography>
          <TextField
            fullWidth
            name="content"
            value={formData.content}
            onChange={handleChange}
            variant="outlined"
            placeholder="Enter the message content"
            multiline
            rows={4}
            size="small"
            error={!!errors.content}
            helperText={errors.content}
            disabled={disabled}
          />
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Label
          </Typography>
          <TextField
            fullWidth
            name="label"
            value={formData.label}
            onChange={handleChange}
            variant="outlined"
            placeholder="Enter a short label (max 40 characters)"
            size="small"
            error={!!errors.label}
            helperText={errors.label || `${formData.label.length}/40 characters`}
            disabled={disabled}
            inputProps={{
              maxLength: 40,
              sx: { py: 1.5 },
            }}
          />
        </Box>
      </Grid>

      {/* Value Type and Applicable For */}
      <Grid item xs={12} sm={6}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Type *
          </Typography>
          <FormControl fullWidth size="small" error={!!errors.value_type}>
            <InputLabel>Select Type</InputLabel>
            <Select
              name="value_type"
              value={formData.value_type}
              onChange={handleChange}
              label="Select Type"
              disabled={disabled}
            >
              {ValueTypeEnums.getEnumValues().map(valueType => (
                <MenuItem key={valueType} value={valueType}>
                  {valueType.toUpperCase().replace('_', ' ')}
                </MenuItem>
              ))}
            </Select>
            {errors.value_type && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.value_type}
              </Typography>
            )}
          </FormControl>
        </Box>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Applicable For *
          </Typography>
          <FormControl fullWidth size="small" error={!!errors.applicable_for}>
            <InputLabel>Select Applicable Options</InputLabel>
            <Select
              multiple
              value={formData.applicable_for}
              onChange={handleApplicableForChange}
              label="Select Applicable Options"
              disabled={disabled}
              renderValue={selected => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map(value => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {applicableForOptions.map(option => (
                <MenuItem key={option} value={option}>
                  <Checkbox
                    checked={formData.applicable_for.indexOf(option) > -1}
                  />
                  {option}
                </MenuItem>
              ))}
            </Select>
            {errors.applicable_for && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.applicable_for}
              </Typography>
            )}
          </FormControl>
        </Box>
      </Grid>

      {/* Expiry Date and Active Status */}
      <Grid item xs={12} sm={6}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Expiry Date *
          </Typography>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DatePicker
              value={
                formData.end_date
                  ? DateTime.fromISO(formData.end_date, { setZone: true }).toLocal()
                  : null
              }
              onChange={handleDateChange}
              minDate={getMinDate()}
              disabled={disabled}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  error: !!errors.end_date,
                  helperText: errors.end_date || 'Required. Cannot select past dates',
                  inputProps: {
                    sx: { py: 1.5 },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Box>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            pt: 3,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                disabled={disabled}
              />
            }
            label="Active"
          />
        </Box>
      </Grid>
    </Grid>
  );
};

export default ValueForm;

